import * as React from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'ramda';

import { getSearchText } from '../../../../selectors/filters';
import { SearchHistoryStorage } from './searchHistoryStorage';
import { SearchTextHistory } from '../../../../components/SearchText';

export interface IParams {
  searchHistoryStorage: SearchHistoryStorage;
  onHistoryItemSelect(selectedItem: string): void;
  onHistoryListOpenChanged?(isOpen: boolean): void;
}

/**
 * Хук создает рендер-функцию, которая рендерит SearchTextHistory - обертка над инпутом,
 * добавляет функционал выпадающего списка с историей поисков
 * */
export const useHistoryListWrapper = ({
  searchHistoryStorage,
  onHistoryItemSelect,
  onHistoryListOpenChanged,
}: IParams) => {
  const [isHistoryListOpened, setHistoryListOpen] = React.useState(false);
  const [isInputFocused, setInputFocused] = React.useState(false);
  const searchText = useSelector(getSearchText);

  const closeHistoryList = React.useCallback(() => setHistoryListOpen(false), []);
  const openHistoryList = React.useCallback(() => setHistoryListOpen(true), []);

  React.useEffect(() => {
    if (onHistoryListOpenChanged) {
      onHistoryListOpenChanged(isHistoryListOpened);
    }
  }, [isHistoryListOpened, onHistoryListOpenChanged]);

  /**
   * При блюре из поискового инпута новая строка сохраняется в историю поисков
   * */
  const handleSearchInputBlur = React.useCallback(() => {
    setInputFocused(false);

    if (searchText) {
      searchHistoryStorage.add(searchText);
    }
  }, [searchHistoryStorage, searchText]);

  const handleSearchInputFocus = React.useCallback(() => {
    setInputFocused(true);

    openHistoryList();
  }, [openHistoryList]);

  const handleSearchInputChange = (value: string) => {
    if (value) {
      closeHistoryList();
    } else {
      openHistoryList();
    }
  };

  const handleHistoryItemSelect = React.useCallback(
    (selectedItem: string) => {
      onHistoryItemSelect(selectedItem);
      closeHistoryList();
    },
    [closeHistoryList, onHistoryItemSelect],
  );

  const handleOutsideHistoryListClick = React.useCallback(() => {
    if (isHistoryListOpened && !isInputFocused) {
      setHistoryListOpen(false);
    }
  }, [isInputFocused, isHistoryListOpened]);

  const searchHistoryList = searchHistoryStorage.list;

  const renderSearchHistoryWrapper = (child: React.ReactElement) => (
    <SearchTextHistory
      isOpen={isHistoryListOpened && !isEmpty(searchHistoryList)}
      searchHistoryList={searchHistoryList}
      onHistoryItemClick={handleHistoryItemSelect}
      onOutsideHistoryListClick={handleOutsideHistoryListClick}
    >
      {child}
    </SearchTextHistory>
  );

  return {
    handleSearchInputBlur,
    handleSearchInputFocus,
    handleSearchInputChange,
    renderSearchHistoryWrapper,
  };
};
